/*
 * Cheat Sheet for the ACP-125 CANSUPP-1(C) manual.
 *
 * As it is a controlled good, we can't include it in this project. However, it
 * is based on the ACP-125(G), which is not a controlled good and is publicly
 * available. It will therefore be used as the publicly accessible reference:
 * https://orwg.cap.gov/media/cms/ACP125GRadioTelephoneProceduresNOV2_EFFE1A51BA783.pdf
 */
import { Floating, StandaloneFloating, useFloating } from './Floating';
import { $codeWords } from '@/stores';
import { $cheatSheet } from '@/stores/floating';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { atom, computed } from 'nanostores';
import { Fragment, type ReactNode } from 'react';

const signalStrength = ['LOUD', 'GOOD', 'WEAK', 'VERY WEAK', 'FADING'];

const readability = [
  'CLEAR',
  'READABLE',
  'UNREADABLE',
  'DISTORTED',
  'WITH INTERFERENCE',
  'INTERMITTENT',
];

const appointments: [string, string][] = [
  ['Commander', 'SUNRAY'],
  ['Chief of Staff', 'MOONBEAM'],
  ['G (Operations) Staff/Adjutant/RCPO', 'SEAGULL'],
  ['G (NBC) Staff', 'BOXWOOD'],
  ['G (Intelligence) Staff', 'ACORN'],
  ['Administrative Staff', 'MANHOLE'],
  ['Logistics Staff Quartermaster', 'MOLAR'],
  ['Q Staff/Equipment Representative', 'NUTSHELL'],
  ['Air Defence Representative', 'CONROD'],
  ['Air Reconnaissance Representative', 'SPYGLASS'],
  ['Air Traffic Controller', 'BASEBALL'],
  ['Air Transport Support Operations Rep', 'ATOLL'],
  ['Land Air Representative', 'HAWKEYE'],
  ['Armour Representative', 'IRONSIDE'],
  ['Armament Officer', 'SHOTGUN'],
  ['Artillery Representative', 'SHELDRAKE'],
  ['Locating Battery Representative', 'CRACKER'],
  ['Engineer/Airfield Construction', 'HOLDFAST'],
  ['Forward Air Controller', 'FORTUNE'],
  ['Ground Liaison Officer', 'GLOWWORM'],
  ['Infantry Representative', 'FOXHOUND'],
  ['Medical Representative', 'STARLIGHT'],
  ['Meteorological Representative', 'METEOR'],
  ['Movements Staff', 'CONTRACTOR'],
  ['Ordnance Representative', 'RICKSHAW'],
  ['Provost Representative', 'WATCH DOG'],
  ['Electrical/Mechanical Engineering Rep', 'BLUEBELL'],
  ['Signals Representative', 'PRONTO'],
  ['Supply and Transport Representative', 'PLAYTIME'],
  ['Chaplain Representative', 'SKYPILOT'],
  ['UAV Representative', 'SKI DOO'],
];

const alphabet = [
  ['A', 'ALFA'],
  ['B', 'BRAVO'],
  ['C', 'CHARLIE'],
  ['D', 'DELTA'],
  ['E', 'ECHO'],
  ['F', 'FOXTROT'],
  ['G', 'GOLF'],
  ['H', 'HOTEL'],
  ['I', 'INDIA'],
  ['J', 'JULIETT'],
  ['K', 'KILO'],
  ['L', 'LIMA'],
  ['M', 'MIKE'],
  ['N', 'NOVEMBER'],
  ['O', 'OSCAR'],
  ['P', 'PAPA'],
  ['Q', 'QUEBEC'],
  ['R', 'ROMEO'],
  ['S', 'SIERRA'],
  ['T', 'TANGO'],
  ['U', 'UNIFORM'],
  ['V', 'VICTOR'],
  ['W', 'WISKEY'],
  ['X', 'XRAY'],
  ['Y', 'YANKEE'],
  ['Z', 'ZULU'],
];

const numerals = [
  'ZE-RO',
  'WUN',
  'TOO',
  'TREE',
  'FOW-ER',
  'FIFE',
  'SIX',
  'SEV-EN',
  'AIT',
  'NIN-ER',
];

const prowords = [
  ['ACKNOWLEDGE (ACK)', 'Instruction to addressee to acknowledge receipt'],
  ['ALL AFTER', 'Portion of message is all the which follows'],
  ['ALL BEFORE', 'Portion of message is all the which preceeds'],
  ['ALL STATIONS', 'All stations on the net, including control station'],
  [
    'ANSWER AFTER',
    'The station called is to answer after call sign ___ when answering transmissions.',
  ],
  ['ASSUME CONTROL', 'You will assume control of the net until further notice'],
  [
    'AUTHENTICATE',
    'The station called is to reply to the challenge which follows',
  ],
  [
    'AUTHENTICATION IS',
    'The transmission authentication of this message is ___',
  ],
  [
    'BREAK BREAK BREAK',
    "'BREAK' spoken thrice means 'Cease transmission immediately. Silence will be retained until the station breaking in has passed the message'",
  ],
  ['CALL SIGN', 'The group that follows is a call sign'],
  ['CLEAR', 'Your signal is of excellent quality'],
  [
    'CLOSE DOWN',
    'Stations are to close down when indicated. Acknowledgements are required',
  ],
  [
    'CONTACT',
    'Contact has been made with the enemy and details will follow ASAP.',
  ],
  ['CORRECT', 'You are correct or what you have said is correct'],
  [
    'CORRECTION',
    `An error has been made in this transmission. Transmission will continue \
with the last word correctly transmitted.

An error has been made in this transmission (or message indicated). The correct \
version is ___.

That which follows is a corrected version in answering your request for verification.`,
  ],
  [
    'DISREGARD THIS TRANSMISSION',
    'This transmission in progress was in error. Disregard it',
  ],
  ['DISTORTED', 'Your transmission is distorted'],
  ['DO NOT ANSWER', 'Stations called are not to answer this call'],
  ['EXECUTE', 'Carry out the action of the message or signal'],
  [
    'EXECUTE TO FOLLOW',
    'Action will be carried out upon receipt of the proword EXECUTE',
  ],
  ['EXEMPT', 'Addressees following are exempt from the collective call'],
  [
    'FADING',
    'Your signal strength fades and continuous reception is unreliable',
  ],
  ['FETCH', 'Indicates to whom the caller wishes to speak'],
  ['FIGURES', 'Numerals or numbers follow'],
  ['FLASH', 'Precedence FLASH'],
  [
    'FROM',
    'The originator of this message is identified by the designator that follows',
  ],
  ['GOOD', 'Your signal strength is good'],
  ['GRID', 'The following information is a grid reference'],
  [
    'I AM ASSUMING CONTROL',
    'I am assuming control of this net until further notice',
  ],
  ['I AUTHENTICATE', 'The group that follows is my authentication reply'],
  ['IMMEDIATE', 'Precedence IMMEDIATE'],
  ['IMMEDIATE EXECUTE', 'Action is to be carried out upon receipt of EXECUTE'],
  ['INFO', 'Addressees following are addressed for information'],
  [
    'INTERMITTENT',
    'Your transmission is intermittent and continuous reception is unreliable',
  ],
  ['I READ BACK', 'The following is my read back of your transmission'],
  ['I SAY AGAIN', 'I am repeating the transmission or portion indicated'],
  ['I SPELL', 'I shall spell the next word phonetically'],
  ['I VERIFY', 'The following has been verified and is repeated'],
  ['LONG MESSAGE', 'The following message will take longer than 30 seconds'],
  ['LOUD', 'Your signal is very strong'],
  ['MESSAGE', 'A message requiring recording is about to follow'],
  ['MESSAGE PASSED', 'The relayed message has been cleared'],
  ['MORE TO FOLLOW', 'I have additional traffic for you'],
  ['NO DUFF', 'This is not exercise traffic and requires immediate action'],
  ['NOTHING HEARD', 'No reply was received from the called station'],
  ['ORDERS', 'A responsible person is to be brought to the radio'],
  ['OUT', 'End of transmission. No reply is expected'],
  ['OVER', 'End of transmission. A reply is required'],
  ['PRIORITY', 'Precedence PRIORITY'],
  ['RADIO CHECK', 'What is my signal strength and readability?'],
  ['READABLE', 'Your signal is readable'],
  ['READ BACK', 'Repeat the transmission exactly as received'],
  ['REBROADCAST YOUR NET', 'Link your nets for automatic rebroadcast'],
  ['RELAY (TO)', 'Transmit this message to the indicated addressees'],
  ['RELAY THROUGH', 'Relay your message through the station indicated'],
  [
    'REPORTING IN TO NET',
    'Joining a previously established net with an assigned call sign',
  ],
  [
    'REQUEST PERMISSION TO JOIN NET',
    'Requesting to join an established net without an assigned call sign',
  ],
  ['REQUEST TIME CHECK', 'Requesting an accurate time check'],
  ['ROGER', 'I have received your last transmission satisfactorily'],
  ['ROUTINE', 'Precedence ROUTINE'],
  [
    'SAY AGAIN',
    'Repeat all or the indicated portion of your last transmission',
  ],
  ['SEND', 'Ready to receive your message'],
  [
    'SILENCE, SILENCE, SILENCE',
    'Cease all transmissions on this net immediately',
  ],
  ['SILENCE LIFTED', 'Normal transmissions may resume'],
  ['SITREP', 'Situation report'],
  ['SPEAKING', 'Used with an appointment title to identify the speaker'],
  ['SPEAK SLOWER', 'Reduce your transmission speed'],
  ['SPELL', 'Spell the word phonetically'],
  [
    'STOP REBROADCASTING',
    'Terminate automatic rebroadcast and resume normal operations',
  ],
  ['THIS IS', 'This transmission is from the station identified next'],
  ['THIS IS A DIRECTED NET', 'This net is now directed'],
  ['THIS IS A FREE NET', 'This net is now free'],
  ['THROUGH ME', 'Relay your message through me'],
  ['TIME', 'The following is a time or date-time group'],
  ['TIME CHECK', 'A time check will follow'],
  ['TO', 'Addressees following are addressed for action'],
  ['UNKNOWN STATION', 'The station I am calling is unknown'],
  ['UNREADABLE', 'I cannot understand your transmission'],
  ['VERIFY', 'Verify the message and provide the correct version'],
  ['VERY WEAK', 'Your signal is very weak'],
  ['WAIT', 'Pause for a few seconds'],
  ['WAIT OUT', 'Pause for longer than a few seconds'],
  ['WEAK', 'Your signal is weak'],
  ['WILCO', 'Received, understood, and will comply'],
  [
    'WITH INTERFERENCE',
    'Having trouble understanding your transmission due to interference',
  ],
  ['WORD AFTER', 'The referenced word is the one following'],
  ['WORD BEFORE', 'The referenced word is the one preceding'],
  ['WORDS TWICE', 'Transmit each phrase or group twice'],
  ['WRONG', 'Your last transmission was incorrect'],
  ['YOUR CALL SIGN IS', 'The following call sign is assigned to you'],
];

function wrapStrings(strings: string[]) {
  return strings.map((s, i) => (
    <div key={i} className="flex items-center">
      <p className="text-sm">{s}</p>
    </div>
  ));
}

type CheatSheetBox = {
  title: string;
  description: ReactNode;
  width: number;
  height: number;
};

const english: CheatSheetBox[] = [
  {
    title: 'Signal Strength',
    description: wrapStrings(signalStrength),
    width: 150,
    height: 175,
  },
  {
    title: 'Readability',
    description: wrapStrings(readability),
    width: 175,
    height: 200,
  },
  {
    title: 'Code Words',
    description: <CodeWordsCheatSheet />,
    width: 300,
    height: 200,
  },
  {
    title: 'Spelling Alphabet',
    description: (
      <div className="grid grid-cols-[auto_auto]">
        {alphabet.map(([a, t], i) => (
          <Fragment key={i}>
            <div className={clsx('border-r px-1', i !== 0 && 'border-t')}>
              {a}
            </div>
            <div className={clsx('px-1', i !== 0 && 'border-t')}>{t}</div>
          </Fragment>
        ))}
      </div>
    ),
    width: 175,
    height: 200,
  },
  {
    title: 'Numerals',
    description: (
      <div className="grid grid-cols-[auto_auto]">
        {numerals.map((t, i) => (
          <Fragment key={i}>
            <div className={clsx('border-r px-1', i !== 0 && 'border-t')}>
              {i + 1}
            </div>
            <div className={clsx('px-1', i !== 0 && 'border-t')}>{t}</div>
          </Fragment>
        ))}
      </div>
    ),
    width: 175,
    height: 200,
  },
  {
    title: 'Appointments',
    description: (
      <div className="grid grid-cols-[auto_auto_auto]">
        {appointments.map(([a, t], i) => (
          <Fragment key={i}>
            <div className={clsx('border-r px-1', i !== 0 && 'border-t')}>
              {i + 1}
            </div>
            <div className={clsx('border-r px-1', i !== 0 && 'border-t')}>
              {a}
            </div>
            <div className={clsx('px-1', i !== 0 && 'border-t')}>{t}</div>
          </Fragment>
        ))}
      </div>
    ),
    width: 400,
    height: 400,
  },
  {
    title: 'Prowords',
    description: (
      <div className="grid grid-cols-[1fr_2fr]">
        {prowords.map(([p, t], i) => (
          <Fragment key={i}>
            <div className={clsx('border-r px-1', i !== 0 && 'border-t')}>
              {p}
            </div>
            <div className={clsx('px-1', i !== 0 && 'border-t')}>{t}</div>
          </Fragment>
        ))}
      </div>
    ),
    width: 400,
    height: 400,
  },
];

const $floatingCheatSheets = atom<CheatSheetBox[]>([]);
const $nonFloatingCheatSheets = computed(
  [$floatingCheatSheets],
  floatingCheatSheets => english.filter(c => !floatingCheatSheets.includes(c)),
);

function CodeWordsCheatSheet() {
  return (
    <div className="grid grid-cols-[auto_auto]">
      {Object.entries(useStore($codeWords)).map(([a, t], i) => (
        <Fragment key={i}>
          <div className={clsx('border-r px-1', i !== 0 && 'border-t')}>
            {a}
          </div>
          <div className={clsx('px-1', i !== 0 && 'border-t')}>{t}</div>
        </Fragment>
      ))}
    </div>
  );
}

export function CheatSheet() {
  const show = useStore($cheatSheet);
  const props = useFloating({
    x: 0,
    y: 0,
    width: 650,
    height: 400,
    visible: $cheatSheet,
  });

  const nonFloatingCheatSheets = useStore($nonFloatingCheatSheets);
  const floatingCheatSheets = useStore($floatingCheatSheets);

  return (
    <Fragment>
      {show && (
        <Floating {...props} title="Cheat Sheet">
          <div className="flex flex-row flex-wrap justify-center gap-1 p-4">
            {nonFloatingCheatSheets.map((s, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 border border-gray-600 p-1"
              >
                <h3 className="flex items-start justify-between border-b border-gray-600 text-lg font-semibold text-gray-800">
                  {s.title}
                  <button
                    onClick={() =>
                      $floatingCheatSheets.set([...floatingCheatSheets, s])
                    }
                    className="cursor-pointer text-gray-700 hover:bg-gray-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="h-4 w-4 fill-current"
                    >
                      <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                    </svg>
                  </button>
                </h3>
                {s.description}
              </div>
            ))}
          </div>
        </Floating>
      )}
      {floatingCheatSheets.map(s => (
        <StandaloneFloating
          key={s.title}
          x={0}
          y={0}
          width={s.width}
          height={s.height}
          title={s.title}
          onClose={() =>
            $floatingCheatSheets.set(floatingCheatSheets.filter(c => c !== s))
          }
        >
          <div className="flex flex-col gap-2 p-1">{s.description}</div>
        </StandaloneFloating>
      ))}
    </Fragment>
  );
}
